import React, { useMemo, useRef, useState } from "react";
import { Box, Typography, Button, TextField, CircularProgress } from "@mui/material";
import { ArrowBack as ArrowBackIcon, Mic as MicIcon, Stop as StopIcon, Check as CheckIcon, Replay as ReplayIcon } from "@mui/icons-material";
import { FormattedMessage, useIntl } from "react-intl";
import useMicRecorder from "./useMicRecorder";
import { pageBackgroundSx, themedFieldSx, ThemeSwitch } from "./interviewTheme";
import { isRateLimited, withRateLimitRetry } from "./rateLimit";
import { backButtonSx } from "./RespondentFormKit";

// End-of-interview REVIEW (day/night themed). Lists every answered turn (question +
// answer). Each answer is editable inline OR by re-recording (mic → transcribe →
// save). An optional "additional comments" recorder is appended as one more answer.
// The advance action flushes any pending edits + the comment, then finalizes the
// session. It only reads as a SUBMIT when it really is the last step: with a Likert
// section still to come (hasLikert) — or, in a two-interview session, a whole second
// interview (hasNextInterview) — it says "Next" instead, because the respondent has
// more ahead of them. In that case onComplete does NOT finalize the session; the
// parent just advances the phase. `hasMore` covers any other stage still to come
// (a reordered flow can put the demographics form after an interview).
const RED = "#e5484d";
const serif = '"Lora", Georgia, "Times New Roman", serif';

// `showComments={false}` drops the "Anything else to add?" box (a voice survey
// has no question to file a free comment under). An item with an `answerNode`
// (a voice survey's choice question) shows that instead of the text answer and
// Re-record: it saves its own changes. Its question heading has the id
// `review-q-<turn_id>`, for the node's aria-labelledby.
export default function ReviewScreen({ title, items, surveyId, token, transcribeAudio, onEditTurn, onAddComment, onComplete, hasLikert = false, hasNextInterview = false, hasMore = false, showComments = true, submitLabel, onBack, backLabel, hint, singleSubmit = false, topBar = true, t, mode, onToggleTheme }) {
    const intl = useIntl();
    const list = useMemo(() => (Array.isArray(items) ? items : []), [items]);
    // Anything still to come turns the submit into a Next.
    const moreToCome = hasLikert || hasNextInterview || hasMore;

    // Local editable text per turn (keyed by turn_id) + the untouched originals so we
    // only ever POST an edit for answers that actually changed.
    const originals = useMemo(() => {
        const m = {};
        list.forEach((it) => {
            if (it.answerNode) return;
            m[it.turn_id] = it.answer || "";
        });
        return m;
    }, [list]);
    const [edits, setEdits] = useState(originals);
    const [comment, setComment] = useState("");
    // turn_id -> "saving" | "waiting" (a 429's Retry-After before the one retry).
    // Any entry here disables Submit, so a pending edit can't be dropped.
    const [pendingIds, setPendingIds] = useState({});
    const [savedIds, setSavedIds] = useState({}); // turn_id -> recently saved ✓
    const [failedIds, setFailedIds] = useState({}); // turn_id -> the edit wasn't saved (text kept)
    const inFlightRef = useRef({}); // turn_id -> the pending save's promise
    const savedTextRef = useRef({}); // turn_id -> last text the BE accepted
    const [submitting, setSubmitting] = useState(false);
    const [recTarget, setRecTarget] = useState(null); // turn_id | "comment" | null
    const recTargetRef = useRef(null);
    recTargetRef.current = recTarget;

    const setPending = (turnId, status) =>
        setPendingIds((p) => {
            const next = { ...p };
            if (status) next[turnId] = status;
            else delete next[turnId];
            return next;
        });

    // Save one edit. Resolves { ok, rateLimited } and never rejects. A 429 waits
    // out Retry-After and retries once; a final failure keeps the text in the
    // field and marks the answer not saved, with Try again.
    const saveEdit = async (turnId, text) => {
        // One save per answer at a time: let a pending one finish first.
        if (inFlightRef.current[turnId]) await inFlightRef.current[turnId];
        const val = (text ?? edits[turnId] ?? "").trim();
        const last = savedTextRef.current[turnId] ?? originals[turnId] ?? "";
        if (val === last.trim()) return { ok: true }; // unchanged → skip
        setPending(turnId, "saving");
        setFailedIds((p) => ({ ...p, [turnId]: false }));
        let rateLimited = false;
        const run = withRateLimitRetry(() => onEditTurn(turnId, val), {
            onWait: () => {
                rateLimited = true;
                setPending(turnId, "waiting");
            },
        })
            .then(() => {
                savedTextRef.current[turnId] = val;
                setSavedIds((p) => ({ ...p, [turnId]: true }));
                return { ok: true };
            })
            .catch((e) => {
                setSavedIds((p) => ({ ...p, [turnId]: false }));
                setFailedIds((p) => ({ ...p, [turnId]: true }));
                return { ok: false, rateLimited: rateLimited || isRateLimited(e) };
            })
            .finally(() => {
                delete inFlightRef.current[turnId];
                setPending(turnId, null);
            });
        inFlightRef.current[turnId] = run;
        return run;
    };
    const anyPending = Object.keys(pendingIds).length > 0;

    // Shared single-turn recorder for re-record + the comment box.
    const mic = useMicRecorder({
        surveyId,
        token,
        transcribeAudio,
        onResult: (text) => {
            const target = recTargetRef.current;
            setRecTarget(null);
            if (target == null) return;
            if (target === "comment") {
                setComment((prev) => (prev ? prev.trim() + " " : "") + text);
                return;
            }
            setEdits((prev) => ({ ...prev, [target]: text }));
            saveEdit(target, text);
        },
        onError: () => setRecTarget(null),
    });

    const isRecording = mic.status === "recording";
    const startRec = (target) => {
        if (recTargetRef.current) return; // one at a time
        setRecTarget(target);
        mic.start();
    };
    const stopRec = () => {
        try {
            mic.stop();
        } catch (e) {
            /* no-op */
        }
    };

    const submit = async () => {
        if (submitting) return;
        setSubmitting(true);
        try {
            // Flush any changed-but-unblurred edits first. Another failed edit
            // still doesn't block finishing, but a rate-limited one that the retry
            // couldn't save does: finishing would silently drop it.
            for (const it of list) {
                if (it.answerNode) continue;
                const res = await saveEdit(it.turn_id);
                if (!res.ok && res.rateLimited) return;
            }
            if (comment.trim()) await onAddComment(comment.trim());
            await onComplete();
        } finally {
            setSubmitting(false);
        }
    };

    const submitPill = (extra) => (
        <Button
            variant="contained"
            disableElevation
            onClick={submit}
            disabled={submitting || anyPending}
            startIcon={submitting ? <CircularProgress size={16} sx={{ color: t.finalBtnText || "#fff" }} /> : null}
            sx={{
                textTransform: "none",
                borderRadius: "999px",
                px: 3,
                py: 1,
                fontWeight: 700,
                whiteSpace: "nowrap",
                backgroundColor: t.finalBtnBg || t.submitBg,
                color: t.finalBtnText || t.submitText,
                boxShadow: t.ctaShadow,
                transition: "transform .15s ease, box-shadow .2s ease, background-color .2s ease",
                "&:hover": { backgroundColor: t.finalBtnBg || t.submitHover, transform: "translateY(-1px)" },
                "&.Mui-disabled": { backgroundColor: t.submitDisabledBg, color: t.submitDisabledText, boxShadow: "none" },
                ...extra,
            }}
        >
            {moreToCome ? (
                <FormattedMessage id="conversation.next" defaultMessage="Next" />
            ) : (
                submitLabel || <FormattedMessage id="conversation.submit_survey" defaultMessage="Submit feedback" />
            )}
        </Button>
    );

    const recordButton = (target) => {
        const activeHere = recTarget === target && isRecording;
        if (activeHere) {
            return (
                <Button
                    variant="outlined"
                    onClick={stopRec}
                    startIcon={<StopIcon />}
                    sx={{ textTransform: "none", borderRadius: "20px", color: RED, borderColor: RED, "&:hover": { borderColor: "#cf3b40", backgroundColor: "rgba(229,72,77,0.06)" } }}
                >
                    <FormattedMessage id="conversation.stop" defaultMessage="Stop" />
                </Button>
            );
        }
        return (
            <Button
                variant="outlined"
                onClick={() => startRec(target)}
                disabled={!!recTarget}
                startIcon={<MicIcon />}
                sx={{ textTransform: "none", borderRadius: "20px", color: t.teal, borderColor: t.pauseBorder, "&:hover": { borderColor: t.teal, backgroundColor: "rgba(14,156,148,0.06)" }, "&.Mui-disabled": { color: t.submitDisabledText, borderColor: t.panelBorder } }}
            >
                <FormattedMessage id="conversation.re_record" defaultMessage="Re-record" />
            </Button>
        );
    };

    return (
        <Box
            sx={{
                minHeight: t.screenMinHeight || "100vh",
                flexGrow: 1,
                display: "flex",
                flexDirection: "column",
                ...pageBackgroundSx(t),
                backgroundAttachment: "fixed",
            }}
        >
            {/* Top bar (a Modern assessment has none: no title, one Submit at the bottom) */}
            {topBar && (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 2,
                        px: { xs: 2, sm: 4 },
                        py: { xs: 1.5, sm: 2 },
                        backgroundColor: t.topBarBg,
                        backdropFilter: "blur(8px)",
                        borderBottom: `1px solid ${t.topBarBorder}`,
                        position: "sticky",
                        top: 0,
                        zIndex: 2,
                    }}
                >
                    <Typography sx={{ fontWeight: 700, color: t.heading, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", pr: 2 }}>
                        {title || <FormattedMessage id="conversation.review_title" defaultMessage="Review your answers" />}
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexShrink: 0 }}>
                        <ThemeSwitch mode={mode} onToggle={onToggleTheme} />
                        {!singleSubmit && submitPill()}
                    </Box>
                </Box>
            )}

            {/* Body */}
            <Box sx={{ flexGrow: 1, width: "100%", maxWidth: 760, mx: "auto", px: { xs: 2, sm: 3 }, py: { xs: 3, sm: 4 } }}>
                <Typography sx={{ fontFamily: serif, color: t.question, fontWeight: 600, fontSize: { xs: "1.5rem", sm: "1.8rem" }, mb: 1 }}>
                    <FormattedMessage id="conversation.review_title" defaultMessage="Review your answers" />
                </Typography>
                <Typography sx={{ color: t.panelMuted, mb: 3, lineHeight: 1.6 }}>
                    {hint ? (
                        hint
                    ) : moreToCome ? (
                        <FormattedMessage
                            id="conversation.review_hint_next"
                            defaultMessage="Here's everything you said. Edit any answer by typing, or tap Re-record to say it again. Add anything else at the bottom, then select Next."
                        />
                    ) : (
                        <FormattedMessage
                            id="conversation.review_hint"
                            defaultMessage="Here's everything you said. Edit any answer by typing, or tap Re-record to say it again. Add anything else at the bottom, then Submit assessment."
                        />
                    )}
                </Typography>

                {list.length === 0 && (
                    <Typography sx={{ color: t.panelMuted, mb: 3 }}>
                        <FormattedMessage id="conversation.review_empty" defaultMessage="Nothing to review yet." />
                    </Typography>
                )}

                {list.map((it, idx) => (
                    <Box
                        key={it.turn_id ?? idx}
                        sx={{ backgroundColor: t.panelBg, border: `1px solid ${t.panelBorder}`, borderRadius: "16px", p: { xs: 2, sm: 2.5 }, mb: 2, boxShadow: t.chipShadow }}
                    >
                        <Typography id={`review-q-${it.turn_id}`} sx={{ fontFamily: serif, color: t.question, fontWeight: 600, fontSize: "1.1rem", mb: 1.25, lineHeight: 1.4 }}>
                            {it.question}
                        </Typography>
                        {it.answerNode || (
                        <>
                        <TextField
                            fullWidth
                            multiline
                            minRows={2}
                            maxRows={10}
                            value={edits[it.turn_id] ?? ""}
                            onChange={(e) => setEdits((prev) => ({ ...prev, [it.turn_id]: e.target.value }))}
                            onBlur={() => saveEdit(it.turn_id)}
                            placeholder={intl.formatMessage({ id: "conversation.review_answer_placeholder", defaultMessage: "Your answer…" })}
                            sx={themedFieldSx(t)}
                        />
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 1.25 }}>
                            {recordButton(it.turn_id)}
                            {pendingIds[it.turn_id] ? (
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, color: t.panelMuted }}>
                                    <CircularProgress size={14} sx={{ color: t.panelMuted }} />
                                    <Typography sx={{ fontSize: "0.8rem" }}>
                                        {pendingIds[it.turn_id] === "waiting" ? (
                                            <FormattedMessage id="conversation.catching_up" defaultMessage="Just a moment, we're catching up…" />
                                        ) : (
                                            <FormattedMessage id="conversation.saving" defaultMessage="Saving…" />
                                        )}
                                    </Typography>
                                </Box>
                            ) : failedIds[it.turn_id] ? (
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                                    <Typography data-testid="edit-not-saved" sx={{ fontSize: "0.8rem", fontWeight: 600, color: RED }}>
                                        <FormattedMessage id="conversation.edit_not_saved" defaultMessage="Your change wasn't saved." />
                                    </Typography>
                                    <Button
                                        size="small"
                                        onClick={() => saveEdit(it.turn_id)}
                                        startIcon={<ReplayIcon sx={{ fontSize: 16 }} />}
                                        sx={{ textTransform: "none", borderRadius: "20px", color: t.teal, fontWeight: 600 }}
                                    >
                                        <FormattedMessage id="conversation.try_again" defaultMessage="Try again" />
                                    </Button>
                                </Box>
                            ) : savedIds[it.turn_id] ? (
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: t.teal }}>
                                    <CheckIcon sx={{ fontSize: 18 }} />
                                    <Typography sx={{ fontSize: "0.8rem", fontWeight: 600 }}>
                                        <FormattedMessage id="conversation.saved_check" defaultMessage="Saved" />
                                    </Typography>
                                </Box>
                            ) : null}
                        </Box>
                        </>
                        )}
                    </Box>
                ))}

                {/* Optional additional comments */}
                {showComments && (
                <Box sx={{ backgroundColor: t.panelBg, border: `1px dashed ${t.panelDashed}`, borderRadius: "14px", p: { xs: 2, sm: 2.5 }, mb: 3 }}>
                    <Typography sx={{ fontWeight: 600, color: t.heading, mb: 0.5 }}>
                        <FormattedMessage id="conversation.review_comments_title" defaultMessage="Anything else to add?" />
                    </Typography>
                    <Typography sx={{ color: t.panelMuted, fontSize: "0.88rem", mb: 1.25 }}>
                        <FormattedMessage id="conversation.review_comments_hint" defaultMessage="Optional — share any final thoughts." />
                    </Typography>
                    <TextField
                        fullWidth
                        multiline
                        minRows={2}
                        maxRows={10}
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        placeholder={intl.formatMessage({ id: "conversation.review_comments_placeholder", defaultMessage: "Add a comment…" })}
                        sx={themedFieldSx(t)}
                    />
                    <Box sx={{ mt: 1.25 }}>{recordButton("comment")}</Box>
                </Box>
                )}

                {isRecording && (
                    <Typography sx={{ color: RED, fontWeight: 600, fontSize: "0.85rem", mb: 2, display: "flex", alignItems: "center", gap: 0.75 }}>
                        <Box component="span" sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: RED, display: "inline-block" }} />
                        <FormattedMessage id="conversation.recording_tap_stop" defaultMessage="Recording — tap Stop when you're done, or just pause." />
                    </Typography>
                )}

                <Box sx={{ display: "flex", alignItems: "center", justifyContent: onBack ? "space-between" : "flex-end", gap: 1.5 }}>
                    {onBack && (
                        <Button onClick={onBack} disabled={submitting || anyPending || !!recTarget} startIcon={<ArrowBackIcon />} data-testid="review-back" sx={backButtonSx(t)}>
                            {backLabel || <FormattedMessage id="voice_survey.back" defaultMessage="Back" />}
                        </Button>
                    )}
                    {submitPill({ px: 4 })}
                </Box>
            </Box>
        </Box>
    );
}
